import { useSeoMeta } from "@unhead/react";
import { Bitcoin, Check, CreditCard, Server, ShieldCheck } from "lucide-react";

import { SiteHeader } from "@/components/cashcraft/SiteHeader";
import { SiteFooter } from "@/components/cashcraft/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useBchPrice, usdToBch } from "@/hooks/useBchPrice";
import { cn } from "@/lib/utils";
import { HOSTING_PLANS, PAYPAL_LINK } from "@/lib/cashcraft";

const Hosting = () => {
  const { data: bchUsd } = useBchPrice();

  useSeoMeta({
    title: "Hosting & care plans · BCH Launch",
    description:
      "Optional monthly hosting and maintenance for your website — fast, secure, and paid in Bitcoin Cash. No lock-in.",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-1/2 top-[-10%] h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-primary/15 blur-[120px]" />
          </div>
          <div className="container mx-auto px-4 py-16 text-center sm:px-6 sm:py-20">
            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
              <Server className="h-4 w-4" />
              Optional hosting & care
            </div>
            <h1 className="mx-auto max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl">
              We'll keep your site{" "}
              <span className="bg-gradient-to-r from-primary to-emerald-400 bg-clip-text text-transparent">
                online & looked after
              </span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              Don't want to deal with hosting, backups, or updates? Let us
              handle it. Simple monthly plans, cancel anytime — paid in Bitcoin
              Cash.
            </p>
          </div>
        </section>

        {/* Plans */}
        <section className="pb-8">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-3">
              {HOSTING_PLANS.map((plan) => (
                <Card
                  key={plan.id}
                  className={cn(
                    "relative flex flex-col border-border/60 bg-card",
                    plan.highlighted &&
                      "border-primary/60 shadow-xl shadow-primary/10 ring-1 ring-primary/30 lg:-translate-y-2",
                  )}
                >
                  {plan.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                      Most popular
                    </div>
                  )}
                  <CardContent className="flex flex-1 flex-col p-8">
                    <h3 className="text-lg font-bold">{plan.name}</h3>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold">
                        ${plan.priceUsd}
                      </span>
                      <span className="text-sm text-muted-foreground">/mo</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-primary">
                      <Bitcoin className="h-3.5 w-3.5" />
                      {bchUsd ? (
                        <span>≈ {usdToBch(plan.priceUsd, bchUsd)} BCH / mo</span>
                      ) : (
                        <span className="text-muted-foreground">
                          loading BCH price…
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground">
                      {plan.tagline}
                    </p>
                    <ul className="mt-6 flex-1 space-y-3">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      asChild
                      className={cn(
                        "mt-8 w-full rounded-full font-semibold",
                        !plan.highlighted &&
                          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                      )}
                    >
                      <a
                        href={PAYPAL_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <CreditCard className="mr-1.5 h-4 w-4" />
                        Subscribe · ${plan.priceUsd}/mo
                      </a>
                    </Button>
                    <p className="mt-2 text-center text-xs text-muted-foreground">
                      Pay the first month (${plan.priceUsd}) via PayPal — or{" "}
                      <a
                        href="/#start"
                        className="text-primary hover:underline"
                      >
                        ask us about BCH
                      </a>
                      .
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* No lock-in note */}
        <section className="pb-20 pt-6">
          <div className="container mx-auto px-4 sm:px-6">
            <Card className="mx-auto max-w-3xl border-dashed border-border bg-muted/20">
              <CardContent className="flex flex-col items-start gap-3 p-6 sm:flex-row sm:items-center sm:gap-4 sm:p-8">
                <ShieldCheck className="h-8 w-8 shrink-0 text-primary" />
                <div>
                  <h3 className="font-bold">Hosting is always optional</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    You own your website outright. Host it yourself if you like —
                    these plans are just here if you'd rather we handle the
                    technical side. Cancel any time; we'll hand everything over.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Hosting;
